import React, { lazy, Suspense, type CSSProperties } from "react";
import type { UnitDetails } from "./types";
import { formatQkindLabel } from "./formatQkindLabel";
import { parseUnitDescription } from "./parseUnitDescription";
import { parseDimensionVector } from "./parseDimensionVector";
import { useUnitLookup } from "./useUnitLookup";
import { Badge } from "components/common/Badge";
import { Tooltip } from "@/components/common/Tooltip";

// React.lazy splits KaTeX (JS + CSS + fonts) into its own chunk so the main
// bundle stays slim. The first unit-with-math hover triggers a one-time
// chunk load; everything else after that is synchronous.
const KatexInline = lazy(() => import("./KatexInline"));

interface UnitInfoCardProps {
  unit: UnitDetails;
  /** QUDT description prose, or `null` if not loaded yet / not present. */
  description: string | null;
  /** When true, the About section renders a skeleton instead of being hidden, so the card doesn't reflow when prose lands. */
  descriptionLoading: boolean;
}

// Surface, border, width, scrolling and the close button all belong to the
// enclosing `DetailsOverlay`; this renders the body only.
const cardStyle: CSSProperties = {
  color: "var(--color-fg)",
  fontSize: "var(--font-size)",
  lineHeight: 1.45,
};

const headStyle: CSSProperties = {
  padding: "var(--spacing-md)",
  // Clears the overlay's floating close button, which sits over this row.
  paddingRight: "var(--spacing-2xl)",
  background: "var(--color-bg-subtle)",
  borderBottom: "var(--size-border-hairline) solid var(--color-border)",
};

const headTopRowStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "var(--spacing-sm)",
};

const titleStyle: CSSProperties = {
  margin: 0,
  // No `flex: 1` — that stretched the title and pushed the kind badge over to
  // the close button. Shrink-to-content keeps the badge beside the name.
  minWidth: 0,
  fontSize: "var(--font-size-large)",
  fontWeight: 600,
  letterSpacing: "-0.005em",
  whiteSpace: "normal",
  wordBreak: "break-word",
};

const symStyle: CSSProperties = {
  display: "inline-block",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--font-size-small)",
  color: "var(--color-accent)",
  marginTop: "var(--spacing-2xs)",
};

const sectionStyle: CSSProperties = {
  padding: "var(--spacing-sm) var(--spacing-md)",
  borderBottom: "var(--size-border-hairline) solid var(--color-border)",
};

const sectionLabelStyle: CSSProperties = {
  margin: 0,
  // Detach the heading from its own rows, which sit 2px apart — at 4px the
  // label read as just another row.
  marginBottom: "var(--spacing-sm)",
  // 9px (`xx-small`) is reserved for canvas column type labels; these are
  // dialog section headings, so they sit at the label step. Uppercase +
  // semibold carries the hierarchy instead of size.
  fontSize: "var(--font-size-small)",
  fontWeight: 600,
  color: "var(--color-fg-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const aboutStyle: CSSProperties = {
  fontSize: "var(--font-size-small)",
  lineHeight: 1.55,
  color: "var(--color-fg)",
};

const rowStyle: CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  fontSize: "var(--font-size-small)",
  padding: "var(--spacing-2xs) 0",
  gap: "var(--spacing-md)",
};

const rowKeyStyle: CSSProperties = {
  color: "var(--color-fg-muted)",
};

const rowValStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  textAlign: "right",
  wordBreak: "break-all",
};

const footStyle: CSSProperties = {
  padding: "var(--spacing-xs) var(--spacing-md)",
  background: "var(--color-bg-subtle)",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  fontSize: "var(--font-size-xx-small)",
  color: "var(--color-fg-muted)",
};

const linkStyle: CSSProperties = {
  color: "var(--color-accent)",
  textDecoration: "none",
  fontFamily: "var(--font-mono)",
  fontSize: "var(--font-size-xx-small)",
};

const equivListStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "var(--spacing-xs)",
  padding: "var(--spacing-2xs) 0",
};

const equivPillStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--spacing-xs)",
  padding: "var(--spacing-2xs) var(--spacing-sm)",
  border: "var(--size-border-hairline) solid var(--color-border)",
  borderRadius: "var(--radius-app)",
  background: "transparent",
  color: "var(--color-fg)",
  textDecoration: "none",
  fontSize: "var(--font-size-small)",
  lineHeight: 1.2,
  minHeight: "var(--control-height-sm)",
  transition:
    "background var(--duration-micro) var(--ease-enter), border-color var(--duration-micro) var(--ease-enter), color var(--duration-micro) var(--ease-enter)",
  boxSizing: "border-box",
};

const equivLabelStyle: CSSProperties = { fontWeight: 500 };

const equivIdStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--font-size-x-small)",
  color: "var(--color-fg-muted)",
};

const equivArrowStyle: CSSProperties = {
  fontSize: "var(--font-size-x-small)",
  color: "var(--color-fg-muted)",
  marginInlineStart: "var(--spacing-2xs)",
};

const skeletonLine: CSSProperties = {
  height: "0.75rem",
  background: "var(--color-bg-hover)",
  borderRadius: "var(--radius-app)",
  marginBottom: "var(--spacing-xs)",
};

const katexFallbackStyle: CSSProperties = {
  fontFamily: "var(--font-mono)",
  fontSize: "var(--font-size-small)",
  color: "var(--color-fg-muted)",
};

// QUDT URIs look like `unit:PDL-PER-FT2`. The QUDT vocab links use the
// `http://qudt.org/vocab/unit/<id>` form. `currency:USD` → ISO 4217 page.
// `custom:*` URIs are MyCorr extensions and have no external destination —
// return null so the footer renders just the source badge + URI as plain
// text, no broken `<a>`. Returns null for any other unknown prefix too.
function externalLinkFor(uri: string): { href: string; label: string } | null {
  if (uri.startsWith("unit:")) {
    const id = uri.slice("unit:".length);
    return { href: `https://qudt.org/vocab/unit/${id}`, label: id };
  }
  if (uri.startsWith("currency:")) {
    const id = uri.slice("currency:".length);
    return {
      href: `https://en.wikipedia.org/wiki/ISO_4217#Active_codes_(list_one)`,
      label: id,
    };
  }
  return null;
}

function sourceLabel(source: UnitDetails["source"], uri: string): string {
  if (source === "qudt") return "QUDT";
  if (source === "custom") return "Custom";
  if (uri.startsWith("currency:")) return "ISO 4217";
  return "source";
}

function uriTail(uri: string): string {
  // "unit:TON_Metric" → "TON_Metric"; defensive fallback for non-prefixed URIs.
  const idx = uri.indexOf(":");
  return idx >= 0 ? uri.slice(idx + 1) : uri;
}

/**
 * Single equivalent-unit pill. Mounted per match URI; calls `useUnitLookup`
 * to hydrate the human label asynchronously. Until the lookup resolves
 * (or if it 404s), the pill shows just the URI tail in monospace — the
 * tail is already a readable slug (`TON_Metric`, `N-PER-M2`), so the UX
 * never blocks waiting for labels.
 */
function EquivalentUnitPill({ uri }: { uri: string }) {
  const lookup = useUnitLookup(uri);
  const link = externalLinkFor(uri);
  const tail = uriTail(uri);
  const label = lookup.details?.label ?? null;
  return (
    <Tooltip title="qudt:exactMatch">
      <a
        href={link?.href ?? uri}
        target="_blank"
        rel="noopener noreferrer"
        style={equivPillStyle}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "var(--color-bg-subtle)";
          e.currentTarget.style.borderColor = "var(--color-accent)";
          e.currentTarget.style.color = "var(--color-accent)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.borderColor = "var(--color-border)";
          e.currentTarget.style.color = "var(--color-fg)";
        }}
      >
        {label && <span style={equivLabelStyle}>{label}</span>}
        <span style={equivIdStyle}>{tail}</span>
        <span style={equivArrowStyle}>↗</span>
      </a>
    </Tooltip>
  );
}

function DescriptionBody({ raw }: { raw: string }) {
  const segs = parseUnitDescription(raw);
  return (
    <>
      {segs.map((s, i) => {
        if (s.kind === "math") {
          return (
            <Suspense
              key={i}
              fallback={<code style={katexFallbackStyle}>{s.tex}</code>}
            >
              <KatexInline tex={s.tex} />
            </Suspense>
          );
        }
        return <React.Fragment key={i}>{s.value}</React.Fragment>;
      })}
    </>
  );
}

/**
 * Hover-card content for a single unit in the catalog. Renders as a sectioned
 * property sheet (header → about → physics → standard codes → source footer).
 * Each section is conditional on field availability — search results that
 * only carry header-level fields collapse to header + about.
 *
 * Takes the already-fetched `description`. Also fires one per-uri lookup to
 * hydrate `exact_matches` lazily — search-result rows ship with an empty
 * array (the search response shape is narrower than `UnitDetails`), so the
 * "Equivalent to" section would otherwise never render for hits found via
 * the search bar. The lookup only fires when the tooltip actually mounts
 * (after the 1.5 s hover delay) and is per-uri cached inside the hook.
 */
export function UnitInfoCard({
  unit,
  description,
  descriptionLoading,
}: UnitInfoCardProps) {
  const dimension = parseDimensionVector(unit.dimension_vector);
  const lookup = useUnitLookup(unit.uri);
  const exactMatches = lookup.details?.exact_matches ?? unit.exact_matches;
  const showAbout =
    descriptionLoading || (description !== null && description.length > 0);
  const showPhysics = Boolean(unit.quantity_kind_label || dimension);
  const showCodes = Boolean(unit.ucum_code || unit.unece_code);
  const showEquivalents = exactMatches.length > 0;
  const link = externalLinkFor(unit.uri);
  const symbol = unit.symbol ?? unit.alpha_code;

  return (
    <div style={cardStyle}>
      <div style={headStyle}>
        <div style={headTopRowStyle}>
          <h3 style={titleStyle}>
            {unit.label ?? unit.alpha_code ?? unit.uri}
          </h3>
          {unit.quantity_kind_label && (
            <Badge
              label={formatQkindLabel(unit.quantity_kind_label)}
              variant="info"
              size="sm"
              uppercase={false}
              className="shrink-0 max-w-32 truncate"
            />
          )}
        </div>
        {symbol && <span style={symStyle}>{symbol}</span>}
      </div>

      {showAbout && (
        <div style={sectionStyle}>
          <h4 style={sectionLabelStyle}>About</h4>
          {descriptionLoading && !description ? (
            <div aria-label="Loading description">
              <div style={{ ...skeletonLine, width: "92%" }} />
              <div style={{ ...skeletonLine, width: "78%" }} />
            </div>
          ) : (
            <div style={aboutStyle}>
              <DescriptionBody raw={description ?? ""} />
            </div>
          )}
        </div>
      )}

      {showPhysics && (
        <div style={sectionStyle}>
          <h4 style={sectionLabelStyle}>Physics</h4>
          {unit.quantity_kind_label && (
            <div style={rowStyle}>
              <span style={rowKeyStyle}>Quantity kind</span>
              <span style={rowValStyle}>
                {formatQkindLabel(unit.quantity_kind_label)}
              </span>
            </div>
          )}
          {dimension && (
            <div style={rowStyle}>
              <span style={rowKeyStyle}>Dimension</span>
              <span style={rowValStyle}>{dimension}</span>
            </div>
          )}
        </div>
      )}

      {showCodes && (
        <div style={sectionStyle}>
          <h4 style={sectionLabelStyle}>Standard codes</h4>
          {unit.ucum_code && (
            <div style={rowStyle}>
              <span style={rowKeyStyle}>UCUM</span>
              <span style={rowValStyle}>{unit.ucum_code}</span>
            </div>
          )}
          {unit.unece_code && (
            <div style={rowStyle}>
              <span style={rowKeyStyle}>UN/ECE</span>
              <span style={rowValStyle}>{unit.unece_code}</span>
            </div>
          )}
        </div>
      )}

      {showEquivalents && (
        <div style={sectionStyle}>
          <h4 style={sectionLabelStyle}>Equivalent to</h4>
          <div style={equivListStyle}>
            {exactMatches.map((matchUri) => (
              <EquivalentUnitPill key={matchUri} uri={matchUri} />
            ))}
          </div>
        </div>
      )}

      <div style={{ ...footStyle, borderBottom: 0 }}>
        <Badge
          label={sourceLabel(unit.source, unit.uri)}
          variant="success"
          size="xs"
        />
        {link && (
          <a
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            style={linkStyle}
          >
            {link.label} ↗
          </a>
        )}
      </div>
    </div>
  );
}
